import { useState, useEffect } from 'react';
import LeftSidebar from './components/LeftSidebar';
import ChatWindow from './components/ChatWindow';
import SettingsModal from './components/SettingsModal';
import { ChatProvider } from './components/ChatContext';
import UserSelectModal from './components/UserSelectModal';
import { User } from './types/api';

interface SelectedItem {
  id: string;
  name: string;
}

interface ProjectChangedEvent extends CustomEvent {
  detail: {
    projectId: string;
    projectName: string;
  };
}

interface VisualizationChangedEvent extends CustomEvent {
  detail: {
    visualizationId: string;
    visualizationName: string;
  };
}

interface VisualizationRenamedEvent extends CustomEvent {
  detail: {
    visualizationId: string;
    visualizationName: string;
  };
}

interface VisualizationDeletedEvent extends CustomEvent {
  detail: {
    visualizationId: string;
  };
}

function AppContent() {
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<SelectedItem>({ id: "", name: "" });
  const [selectedVisualization, setSelectedVisualization] = useState<SelectedItem>({ id: "", name: "" });

  useEffect(() => {
    const checkUserSelected = () => {
      const userObj = localStorage.getItem('userSelectedObj');
      if (!userObj) {
        setShowUserModal(true);
      } else {
        setShowUserModal(false);
      }
    };
    checkUserSelected();
    window.addEventListener('userChanged', checkUserSelected);
    return () => window.removeEventListener('userChanged', checkUserSelected);
  }, []);

  useEffect(() => {
    if (showUserModal) {
      fetch('/api/users')
        .then(res => res.json())
        .then(data => setUsers(data))
        .catch(() => setUsers([]));
    }
  }, [showUserModal]);

  const handleUserSelect = (user: User) => {
    // Clear cached project and previous user
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('userSelectedObj');
    // Set new user
    localStorage.setItem('userSelected', 'true');
    localStorage.setItem('userSelectedObj', JSON.stringify(user));
    // Reset selected project and visualization state
    setSelectedProject({ id: '', name: '' });
    setSelectedVisualization({ id: '', name: '' });
    setShowUserModal(false);
    window.dispatchEvent(new Event('userChanged'));
  };

  // Listen for userChanged event
  useEffect(() => {
    const updateUser = () => {
      try {
        const userObj = localStorage.getItem('userSelectedObj');
        if (userObj) {
          const parsed = JSON.parse(userObj);
          setSelectedUserName(parsed.username || parsed.name || "");
        } else {
          setSelectedUserName("");
        }
      } catch {
        setSelectedUserName("");
      }
    };
    updateUser();
    window.addEventListener("userChanged", updateUser);
    return () => window.removeEventListener("userChanged", updateUser);
  }, []);

  // Listen for projectChanged event
  useEffect(() => {
    const handleProjectChanged = (e: ProjectChangedEvent) => {
      const { projectId, projectName } = e.detail;
      localStorage.setItem('selectedProject', JSON.stringify({ projectId, projectName }));
      setSelectedProject({ id: projectId, name: projectName || "" });
      setSelectedVisualization({ id: "", name: "" });
      console.log('Project changed:', e.detail);
    };
    window.addEventListener('projectChanged', handleProjectChanged as EventListener);
    return () => window.removeEventListener('projectChanged', handleProjectChanged as EventListener);
  }, []);

  // Listen for visualizationChanged event
  useEffect(() => {
    const handleVisualizationChanged = (e: VisualizationChangedEvent) => {
      setSelectedVisualization({
        id: e.detail.visualizationId,
        name: e.detail.visualizationName || ""
      });
      console.log('Visualization changed:', e.detail);
    };
    window.addEventListener('visualizationChanged', handleVisualizationChanged as EventListener);
    return () => window.removeEventListener('visualizationChanged', handleVisualizationChanged as EventListener);
  }, []);

  // Listen for visualizationRenamed event
  useEffect(() => {
    const handleVisualizationRenamed = (e: VisualizationRenamedEvent) => {
      if (selectedVisualization.id === e.detail.visualizationId) {
        setSelectedVisualization({ id: e.detail.visualizationId, name: e.detail.visualizationName });
      }
      console.log('Visualization renamed:', e.detail);
    };
    window.addEventListener('visualizationRenamed', handleVisualizationRenamed as EventListener);
    return () => window.removeEventListener('visualizationRenamed', handleVisualizationRenamed as EventListener);
  }, [selectedVisualization.id]);

  // Listen for visualizationDeleted event
  useEffect(() => {
    const handleVisualizationDeleted = (e: VisualizationDeletedEvent) => {
      if (selectedVisualization.id === e.detail.visualizationId) {
        setSelectedVisualization({ id: '', name: '' });
      }
      console.log('Visualization deleted:', e.detail);
    };
    window.addEventListener('visualizationDeleted', handleVisualizationDeleted as EventListener);
    return () => window.removeEventListener('visualizationDeleted', handleVisualizationDeleted as EventListener);
  }, [selectedVisualization.id]);

  return (
    <div className="flex h-screen overflow-hidden">
      <LeftSidebar
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenUserModal={() => setShowUserModal(true)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-5 border-b-2 border-black bg-uiBackground flex items-center gap-6 min-h-[64px]">
            {selectedProject.id ? (
              <span className="text-xl font-medium">{selectedProject.name}</span>
            ) : (
              <span className="text-xl font-medium text-black opacity-60">Please select a project</span>
            )}
            {selectedProject.id && selectedVisualization.id && (
              <span className="mx-1 text-xl font-medium text-black">-</span>
            )}
            {selectedVisualization.id && (
              <span className="text-xl font-medium text-black">{selectedVisualization.name}</span>
            )}
            {selectedUserName && (
              <span className="ml-auto text-base text-black opacity-60 italic">{selectedUserName}</span>
            )}
        </div>
        <ChatWindow />
      </div>
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <UserSelectModal open={showUserModal} onSelect={handleUserSelect} users={users} />
    </div>
  );
}

export default function App() {
  return (
    <ChatProvider>
      <AppContent />
    </ChatProvider>
  );
}
